/**
 * CheckUp schemas
 */

import { z } from 'zod';

/**
 * Motivational answer schema
 */
export const motivationalAnswerSchema = z.object({
  questionId: z.string(),
  /**
   * An empty string is valid (R7, 2026-09-10). Every motivational question is
   * optional, so refusing a blank answer here would reject a submission the
   * product explicitly permits. Thin input is handled by scoring confidence
   * (`assessMotivationalSignal` in @tonnie/heroes), never by rejection.
   */
  answer: z.union([z.string(), z.number(), z.array(z.string())]),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Submit motivational answers schema
 */
export const submitMotivationalAnswersSchema = z.object({
  answers: z.array(motivationalAnswerSchema).min(1),
});

// ═══════════════════════════════════════════════════════════════════════
// PITCH RECORDER — the single source of truth for the recording budget
// ═══════════════════════════════════════════════════════════════════════

/**
 * Maximum pitch-recording length, in seconds.
 *
 * THE one source of the limit. The number used to live in four places and one
 * of them disagreed (the controller, the brain guide config's `maxDuration`, the
 * recorder widget's fallback, and a `180` in this file under another field
 * name). The brain config copy is gone (F13) — the recorder reads this constant
 * and nothing else — and every "is this too long?" question is answered by
 * {@link isPitchTooLong} below, on the client before upload and on the server at
 * confirm and at submit.
 */
export const PITCH_MAX_DURATION_SECONDS = 300;

/**
 * Whether a recording of `seconds` exceeds the pitch limit (F13 · D-1).
 *
 * `floor`, not `round` or `ceil`: a take the recorder stopped at its deadline
 * measures a few hundred milliseconds over 300 (the stop is asynchronous) and
 * is a 5-minute pitch; a picked file of 300.9 s is too. 301 s is not. The
 * client refuses a too-long picked file BEFORE it uploads a byte, and the
 * server refuses it at confirm (container duration) and at submit (the claim),
 * with the same answer because both call this.
 */
export function isPitchTooLong(seconds: number): boolean {
  return Math.floor(seconds) > PITCH_MAX_DURATION_SECONDS;
}

/**
 * Explicit video bitrate for the pitch `MediaRecorder`, in bits per second.
 *
 * ROOT CAUSE of the 76 MB WebM files. The recorder was constructed with
 * `{ mimeType }` alone, so the encoder bitrate was entirely browser-chosen:
 * 24 production pitch videos average 31 MB and the four largest are 76 MB —
 * and all four are stuck in `uploading`, because >50 MB fails 4 times in 5.
 * Capping the resolution/duration would not have helped; an unbounded bitrate
 * makes the size of a fixed-length recording unpredictable by design.
 *
 * 600 kbps is a standard VP8/VP9 target for a 720p webcam talking head — one
 * mostly-static subject, no camera motion, no scene cuts. Together with
 * {@link PITCH_AUDIO_BITS_PER_SECOND} a full-length take lands near 25 MB
 * instead of anywhere between 8 MB and 76 MB.
 *
 * NOTE — this number is pinned on ENCODE QUALITY, deliberately not on any
 * downstream analysis ceiling. The "~25 MB transcription limit" is asserted
 * only in code comments and enforced only on the CoPilot path; W5 §2.3(2)
 * requires it to be swept on staging before anything is pinned to it. If that
 * sweep returns a real number, revisit this pair — do not assume 25 MB.
 */
export const PITCH_VIDEO_BITS_PER_SECOND = 600_000;

/** Explicit audio bitrate for the pitch `MediaRecorder` — speech-grade Opus. */
export const PITCH_AUDIO_BITS_PER_SECOND = 64_000;

/**
 * Submit pitch video schema — ONE schema, both sides.
 *
 * `duration` is whole seconds and `0` means "unknown" (a picked file whose
 * container the browser could not read). There is deliberately no `.max()`:
 * a pitch over the limit is a DOMAIN refusal with its own typed error
 * (`CHECKUP_PITCH_TOO_LONG`, decided by {@link isPitchTooLong}) that the client
 * can explain to the citizen — a schema cap produced a generic 400 the client
 * could only render as "upload failed". The server also checks the length it
 * read from the container header of the bytes that actually landed
 * (`VaultConfirmService` → `media-duration.ts`), not only this claim.
 */
export const submitPitchVideoSchema = z.object({
  fileId: z.uuid(),
  duration: z.number().int().min(0),
});

/**
 * Start CheckUp schema
 */
export const startCheckUpSchema = z.object({
  journeyInstanceId: z.uuid(),
});

// Type exports
export type MotivationalAnswerInput = z.infer<typeof motivationalAnswerSchema>;
export type SubmitMotivationalAnswersInput = z.infer<typeof submitMotivationalAnswersSchema>;
export type SubmitPitchVideoInput = z.infer<typeof submitPitchVideoSchema>;
export type StartCheckUpInput = z.infer<typeof startCheckUpSchema>;
